import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { DEFAULT_CURRENCY_CODE } from '@/utils/money';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { OrderEntryMobileCatalogSearchBar } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-catalog-search-bar';
import { OrderEntryMobileCheckoutSheet } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/containers/order-entry-mobile-checkout-sheet';
import { OrderEntryMobileFulfillmentPushView } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-fulfillment-push-view';
import { OrderEntryMobileGuidePicker } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-guide-picker';
import { OrderEntryMobileHeader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-header';
import { OrderEntryMobileHeaderPanel } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-header-panel';
import { OrderEntryMobileHeaderPanelDetails } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-header-panel-details';
import { OrderEntryMobileHeaderPanelSkeleton } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-header-panel-skeleton';
import { OrderEntryMobileHeaderSkeleton } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-header-skeleton';
import { OrderEntryMobileOnHoldBanner } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-on-hold-banner';
import { OrderEntryMobileRestaurantsPushView } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-restaurants-push-view';
import { OrderEntryMobileSearchContainer } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/containers/order-entry-mobile-search-container';
import { OrderEntryMobileSearchPill } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-search-pill';
import { OrderEntryMobileSkeletonList } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-skeleton-list';
import { OrderEntryMobileSummaryBar } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/containers/order-entry-mobile-summary-bar';
import { OrderEntryMode } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/types';
import {
  OrderMetricKey,
  useOrderMetrics,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-metrics';
import { PageLayout, YStack } from '@usepepper/sprout-kit-ui';
import { partnerSupplier } from '@/utils/environment';
import { RestaurantListData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/sales-portal-restaurant-transformer';
import { SalesPortalStackParams } from '@/navigators/types';
import {
  selectSalesPortalCartItems,
  selectSalesPortalCartOrderId,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAuthenticatedUserUUID } from '@/contexts/auth-context';
import { useBottomNavScrollHandler } from '@sales-portal/blocs/hooks/use-bottom-nav-scroll-handler';
import { useDebouncedCallback } from 'use-debounce';
import { useDeferredUnmount } from '@/experiences/sales-portal/blocs/hooks/use-deferred-unmount';
import { useExperienceUIStore } from '@experiences/blocs/stores/experience-ui-store';
import { useFulfillmentSelection } from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-fulfillment-selection';
import { useNavigation } from '@react-navigation/native';
import { useOrderEntryCommissionLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-order-entry-commission-loader';
import { useOrderEntryMobileData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-mobile-data';
import { useOrderEntryMobileFulfillmentPicker } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-entry-mobile-fulfillment-picker';
import { useSalesPortalRestaurantListLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-sales-portal-restaurant-list-loader';
import { useSaveOrderFulfillmentSettings } from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-save-order-fulfillment-settings';
import { useSaveSalesPortalCartMutator } from '@/experiences/sales-portal/domain/ordering/blocs/mutators/use-save-sales-portal-cart-mutator';
import { useShallow } from 'zustand/react/shallow';
import { useTheme } from '@/contexts/theme-context';

interface OrderEntryMobileContentProps {
  restaurantUUID: string;
  supplierUUID: string;
}

const CART_SAVE_DEBOUNCE_MS = 600;

/** Summary-bar order from the mobile design: GP%, GP, Commission / Items, Total.
 *  Keys absent from this list (e.g. total_units) are not shown on phone. */
const BAR_METRIC_ORDER: OrderMetricKey[] = [
  'gp_percentage',
  'gp_amount',
  'commission',
  'total_items',
  'subtotal',
];

interface PickedRestaurant {
  restaurantName: string;
  restaurantUUID: string;
}

export const OrderEntryMobileContent: React.FC<
  OrderEntryMobileContentProps
> = ({ restaurantUUID, supplierUUID }) => {
  const theme = useTheme();
  const handleBottomNavScroll = useBottomNavScrollHandler();
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [isRestaurantSelectorOpen, setIsRestaurantSelectorOpen] =
    useState(false);
  const [nextSelectedRestaurant, setNextSelectedRestaurant] =
    useState<PickedRestaurant | null>(null);

  const {
    activeOrderGuide,
    additionalOrderValidationRoles,
    areSubstitutionsEnabled,
    availabilityConstraints,
    availableFulfillments,
    employeeRole,
    error,
    isCommissionsEnabled,
    isGrossProfitHidden,
    loading,
    loaderArgs,
    onHold,
    onSelectOrderGuide,
    orderGuides,
    refetch,
    savedSelectedFulfillment,
    saveSelectedFulfillment,
    selectedRestaurant,
    showConfidentialFields,
    timezones,
  } = useOrderEntryMobileData({ restaurantUUID, supplierUUID });

  const {
    error: restaurantListError,
    fetchingMore: isFetchingMoreRestaurants,
    fetchMore: fetchMoreRestaurants,
    hasMore: hasMoreRestaurants,
    loading: restaurantListLoading,
    onClear: onClearRestaurantSearch,
    onSearch: onSearchRestaurants,
    refetch: refetchRestaurantList,
    restaurantListData,
  } = useSalesPortalRestaurantListLoader(
    partnerSupplier.businessOrganizationUUID,
  );

  const authenticatedUserUUID = useAuthenticatedUserUUID();
  const orderUUID = useCartStore(useShallow(selectSalesPortalCartOrderId));
  const { commissionDisplayValue, isCommissionsLoading, refetchCommission } =
    useOrderEntryCommissionLoader({
      authenticatedUserUUID,
      currencyCode: selectedRestaurant?.currencyCode ?? DEFAULT_CURRENCY_CODE,
      enabled: isCommissionsEnabled,
      orderUUID,
    });

  const [isCommissionStale, setIsCommissionStale] = useState(false);

  const saveCart = useSaveSalesPortalCartMutator();
  const debouncedSaveCart = useDebouncedCallback(async () => {
    try {
      if (await saveCart()) {
        await refetchCommission?.();
      }
    } finally {
      setIsCommissionStale(false);
    }
  }, CART_SAVE_DEBOUNCE_MS);

  const handleCartChange = useMemo(
    () =>
      Object.assign(
        () => {
          setIsCommissionStale(true);
          debouncedSaveCart();
        },
        { flush: debouncedSaveCart.flush },
      ),
    [debouncedSaveCart],
  );

  const navigation =
    useNavigation<StackNavigationProp<SalesPortalStackParams>>();

  const handleOpenHeader = useCallback(() => setIsHeaderExpanded(true), []);
  const handleCloseHeader = useCallback(() => setIsHeaderExpanded(false), []);
  const handleOpenRestaurantSelector = useCallback(
    () => setIsRestaurantSelectorOpen(true),
    [],
  );
  const handleCloseRestaurantSelector = useCallback(() => {
    setIsRestaurantSelectorOpen(false);
    onClearRestaurantSearch();
  }, [onClearRestaurantSearch]);

  // Using `setParams` over `navigation.replace` to keep the screen mounted,
  // and avoid a flicker of state for open menus and views. Also avoids rewriting
  // the `supplierUUID` for the same reason.
  const handleSelectRestaurant = useCallback(
    (restaurant: RestaurantListData) => {
      debouncedSaveCart.flush();
      useCartStore.getState().resetCart();

      setNextSelectedRestaurant({
        restaurantName: restaurant.restaurantName,
        restaurantUUID: restaurant.key,
      });
      navigation.setParams({
        restaurantUUID: restaurant.key,
        ...(restaurant.supplierUUID === supplierUUID
          ? null
          : { supplierUUID: restaurant.supplierUUID }),
      });
      setIsRestaurantSelectorOpen(false);

      // Reopening the picker should show the unfiltered list.
      onClearRestaurantSearch();
    },
    [debouncedSaveCart, navigation, onClearRestaurantSearch, supplierUUID],
  );

  const handleFetchMoreRestaurants = useCallback(() => {
    if (hasMoreRestaurants && !isFetchingMoreRestaurants) {
      fetchMoreRestaurants();
    }
  }, [fetchMoreRestaurants, hasMoreRestaurants, isFetchingMoreRestaurants]);

  const fulfillmentSelection = useFulfillmentSelection({
    additionalOrderValidationRoles,
    availableFulfillments,
    employeeRole,
    savedSelectedFulfillment,
    timezones,
  });

  const { saveFulfillment } = useSaveOrderFulfillmentSettings({
    saveSelectedFulfillment,
  });

  const {
    closePicker: closeFulfillmentPicker,
    isPickerOpen: isFulfillmentPickerOpen,
    openPicker: openFulfillmentPicker,
    shouldRenderPicker: shouldRenderFulfillmentPicker,
  } = useOrderEntryMobileFulfillmentPicker();

  const [isGuidePickerOpen, setIsGuidePickerOpen] = useState(false);
  const handleOpenGuidePicker = useCallback(
    () => setIsGuidePickerOpen(true),
    [],
  );
  const handleCloseGuidePicker = useCallback(
    () => setIsGuidePickerOpen(false),
    [],
  );

  // We're maintaining which restaurant was selected, while waiting for the data of the
  // newly selected restaurant to load. That way we can display the new restaurant name
  // in the header, despite the rest of the state not being caught up yet.
  const hasSelectedNewRestaurant =
    nextSelectedRestaurant !== null &&
    nextSelectedRestaurant.restaurantUUID === restaurantUUID;
  const hasResolvedNewRestaurant =
    selectedRestaurant !== undefined &&
    selectedRestaurant.key === restaurantUUID;
  const headerRestaurantName = hasSelectedNewRestaurant
    ? nextSelectedRestaurant.restaurantName
    : hasResolvedNewRestaurant
      ? selectedRestaurant.restaurantName
      : null;

  const hasHeaderDetails =
    !loading &&
    selectedRestaurant !== undefined &&
    savedSelectedFulfillment !== null &&
    activeOrderGuide !== null;
  const isLoadingDetails = !hasHeaderDetails && !error;
  const isHeaderReady = hasHeaderDetails && !error;

  const panelDetails = hasHeaderDetails ? (
    <OrderEntryMobileHeaderPanelDetails
      availableFulfillments={availableFulfillments}
      cutoffTimeString={availabilityConstraints?.cutoffTimeString}
      isFulfillmentDisabled={onHold}
      onPressFulfillment={onHold ? undefined : openFulfillmentPicker}
      onPressOrderGuide={
        orderGuides.length > 1 ? handleOpenGuidePicker : undefined
      }
      orderGuideName={activeOrderGuide.displayName}
      orderMinimumDisplayString={
        availabilityConstraints?.orderMinimumDisplayString
      }
      savedSelectedFulfillment={savedSelectedFulfillment}
      timezones={timezones}
    />
  ) : isLoadingDetails ? (
    <OrderEntryMobileHeaderPanelSkeleton />
  ) : null;

  let headerSlot: React.ReactNode = null;
  if (isHeaderReady) {
    headerSlot = (
      <OrderEntryMobileHeader
        availableFulfillments={availableFulfillments}
        isExpanded={isHeaderExpanded}
        onPress={handleOpenHeader}
        orderGuideName={activeOrderGuide.displayName}
        restaurantName={selectedRestaurant.restaurantName}
        restaurantUUID={selectedRestaurant.key}
        savedSelectedFulfillment={savedSelectedFulfillment}
        timezones={timezones}
      />
    );
  } else if (!error) {
    headerSlot = <OrderEntryMobileHeaderSkeleton />;
  }

  const cartItems = useCartStore(
    useShallow((state) => selectSalesPortalCartItems(state)),
  );
  const orderMetrics = useOrderMetrics(cartItems, {
    commission: {
      displayValue: commissionDisplayValue,
      isEnabled: isCommissionsEnabled,
      isLoading: isCommissionsLoading || isCommissionStale,
    },
    currencyCode: selectedRestaurant?.currencyCode ?? DEFAULT_CURRENCY_CODE,
    isGrossProfitHidden,
  });
  const barMetrics = useMemo(() => {
    const metricsByKey = new Map(
      orderMetrics.map((metric) => [metric.metricKey, metric]),
    );
    return BAR_METRIC_ORDER.flatMap((metricKey) => {
      const metric = metricsByKey.get(metricKey);
      if (!metric) {
        return [];
      }
      if (metricKey === 'total_items') {
        return [
          {
            ...metric,
            title: i18n.t(
              'sales_portal.order_entry.order_summary_bottom_bar.metric.items.title',
            ),
          },
        ];
      }
      if (metricKey === 'subtotal') {
        return [
          {
            ...metric,
            title: i18n.t(
              'sales_portal.order_entry.order_summary_bottom_bar.metric.total.title',
            ),
          },
        ];
      }
      return [metric];
    });
  }, [orderMetrics]);
  const bottomNavHeight = useExperienceUIStore(
    (state) => state.bottomNavHeight,
  );

  const showSummaryBar = !onHold && !error && !loading && !!loaderArgs;

  const [catalogSearchCta, setCatalogSearchCta] = useState<{
    isEnabled: boolean;
    isVisible: boolean;
    onPress: () => void;
  } | null>(null);
  const [searchPill, setSearchPill] = useState<{
    isVisible: boolean;
    onPress: () => void;
  } | null>(null);

  const [orderEntryMode, setOrderEntryMode] = useState<OrderEntryMode>(
    OrderEntryMode.Build,
  );
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const handleOpenCheckout = useCallback(() => setIsCheckoutOpen(true), []);
  const handleCloseCheckout = useCallback(() => setIsCheckoutOpen(false), []);
  const handleOrderPlaced = useCallback(
    () => setOrderEntryMode(OrderEntryMode.Build),
    [],
  );

  const shouldRenderCheckout = useDeferredUnmount(isCheckoutOpen);
  const shouldRenderGuidePicker = useDeferredUnmount(isGuidePickerOpen);
  const shouldRenderRestaurantSelector = useDeferredUnmount(
    isRestaurantSelectorOpen,
  );
  const [isBarExpanded, setIsBarExpanded] = useState(false);
  const [barClearance, setBarClearance] = useState(0);
  const [barPanelHeight, setBarPanelHeight] = useState(0);
  const handleToggleBarExpanded = useCallback(() => {
    setIsBarExpanded((current) => !current);
  }, []);

  const bottomInset =
    bottomNavHeight +
    (showSummaryBar ? barClearance + (isBarExpanded ? barPanelHeight : 0) : 0);

  let body: React.ReactNode;
  if (error) {
    body = (
      <YStack flex={1}>
        <ErrorState
          description={i18n.t(
            'sales_portal.order_entry.error.table.description',
          )}
          onPress={refetch}
          title={i18n.t('sales_portal.order_entry.error.table.title')}
        />
      </YStack>
    );
  } else if (loading || !loaderArgs) {
    body = (
      <YStack flex={1} padding={theme.sk.spacing.spaceMedium}>
        <OrderEntryMobileSkeletonList />
      </YStack>
    );
  } else {
    body = (
      <OrderEntryMobileSearchContainer
        areSubstitutionsEnabled={areSubstitutionsEnabled}
        bottomInset={bottomInset}
        debouncedSaveCart={handleCartChange}
        isGrossProfitHidden={isGrossProfitHidden}
        isQuantityDisabled={onHold}
        loaderArgs={loaderArgs}
        mode={orderEntryMode}
        onCatalogSearchCtaChange={setCatalogSearchCta}
        onSearchPillChange={setSearchPill}
        showConfidentialFields={showConfidentialFields}
      />
    );
  }

  return (
    <>
      <PageLayout
        onBodyScroll={handleBottomNavScroll}
        overlay={
          headerRestaurantName !== null ? (
            <>
              <OrderEntryMobileHeaderPanel
                details={panelDetails}
                isOpen={isHeaderExpanded}
                onClose={handleCloseHeader}
                onSelectRestaurant={handleOpenRestaurantSelector}
                restaurantName={headerRestaurantName}
                restaurantUUID={restaurantUUID}
              />
              {shouldRenderRestaurantSelector ? (
                <OrderEntryMobileRestaurantsPushView
                  hasError={!!restaurantListError}
                  isFetchingMore={isFetchingMoreRestaurants}
                  isLoading={restaurantListLoading}
                  isOpen={isRestaurantSelectorOpen}
                  onClearSearch={onClearRestaurantSearch}
                  onClose={handleCloseRestaurantSelector}
                  onEndReached={handleFetchMoreRestaurants}
                  onRetry={refetchRestaurantList}
                  onSearch={onSearchRestaurants}
                  onSelect={handleSelectRestaurant}
                  restaurants={restaurantListData}
                  selectedRestaurantUUID={restaurantUUID}
                />
              ) : null}
            </>
          ) : null
        }
        scroll={false}
      >
        <YStack
          aria-hidden={isHeaderExpanded}
          flex={1}
          importantForAccessibility={
            isHeaderExpanded ? 'no-hide-descendants' : 'auto'
          }
          pointerEvents={isHeaderExpanded ? 'none' : 'auto'}
        >
          {headerSlot}
          {onHold && !loading && !error && loaderArgs ? (
            <OrderEntryMobileOnHoldBanner />
          ) : null}
          {body}
          {showSummaryBar ? (
            <OrderEntryMobileSummaryBar
              expanded={isBarExpanded}
              hasItemsInCart={cartItems.length > 0}
              metrics={barMetrics}
              mode={orderEntryMode}
              onCheckout={handleOpenCheckout}
              onClearanceChange={setBarClearance}
              onModeChange={setOrderEntryMode}
              onPanelHeightChange={setBarPanelHeight}
              onToggleExpanded={handleToggleBarExpanded}
              searchPillElement={
                searchPill ? (
                  <OrderEntryMobileSearchPill
                    isVisible={!isBarExpanded && searchPill.isVisible}
                    onPress={searchPill.onPress}
                  />
                ) : undefined
              }
            />
          ) : null}
          {showSummaryBar && selectedRestaurant && shouldRenderCheckout ? (
            <OrderEntryMobileCheckoutSheet
              availabilityConstraints={availabilityConstraints}
              availableFulfillments={availableFulfillments}
              commissionDisplayValue={commissionDisplayValue}
              fulfillmentSelection={fulfillmentSelection}
              isGrossProfitHidden={isGrossProfitHidden}
              onClose={handleCloseCheckout}
              onOrderPlaced={handleOrderPlaced}
              onSaveFulfillment={saveFulfillment}
              open={isCheckoutOpen}
              refetchCommission={refetchCommission}
              restaurantUUID={restaurantUUID}
              savedSelectedFulfillment={savedSelectedFulfillment}
              selectedRestaurant={selectedRestaurant}
              supplierUUID={supplierUUID}
              timezones={timezones}
            />
          ) : null}
          {shouldRenderGuidePicker ? (
            <OrderEntryMobileGuidePicker
              onClose={handleCloseGuidePicker}
              onSelect={onSelectOrderGuide}
              open={isGuidePickerOpen}
              orderGuides={orderGuides}
              selectedOrderGuideUUID={activeOrderGuide?.uuid}
            />
          ) : null}
          {savedSelectedFulfillment && shouldRenderFulfillmentPicker ? (
            <OrderEntryMobileFulfillmentPushView
              availableFulfillments={availableFulfillments}
              createIsDateDisabled={fulfillmentSelection.createIsDateDisabled}
              getTimeslotCategories={fulfillmentSelection.getTimeslotCategories}
              onClose={closeFulfillmentPicker}
              onSave={saveFulfillment}
              open={isFulfillmentPickerOpen}
              savedSelectedFulfillment={savedSelectedFulfillment}
              timezones={timezones}
              validateFulfillmentDate={
                fulfillmentSelection.validateFulfillmentDate
              }
              validateTimeslot={fulfillmentSelection.validateTimeslot}
            />
          ) : null}
        </YStack>
      </PageLayout>
      {catalogSearchCta ? (
        <OrderEntryMobileCatalogSearchBar
          isEnabled={catalogSearchCta.isEnabled}
          isVisible={catalogSearchCta.isVisible}
          onPress={catalogSearchCta.onPress}
        />
      ) : null}
    </>
  );
};
